import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, HeartHandshake, Users, GraduationCap, Leaf, ShieldCheck } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const Programs = () => {
  const programs = [
    {
      id: 1,
      title: 'Education for All',
      description: 'Providing quality education to underprivileged children through our network of learning centers and scholarship programs.',
      icon: <BookOpen className="w-8 h-8 text-[#da291c]" />,
      stats: '5000+ Students',
      link: '/programs/education'
    },
    {
      id: 2,
      title: 'Healthcare Initiative',
      description: 'Ensuring access to basic healthcare services and medical camps in rural and underserved communities.',
      icon: <HeartHandshake className="w-8 h-8 text-[#da291c]" />,
      stats: '200+ Camps',
      link: '/programs/healthcare'
    },
    {
      id: 3,
      title: 'Women Empowerment',
      description: 'Empowering women through skill development, entrepreneurship training, and financial literacy programs.',
      icon: <Users className="w-8 h-8 text-[#da291c]" />,
      stats: '1000+ Women Trained',
      link: '/programs/women-empowerment'
    },
    {
      id: 4,
      title: 'Youth Development',
      description: 'Nurturing young minds through leadership programs, career guidance, and extracurricular activities.',
      icon: <GraduationCap className="w-8 h-8 text-[#da291c]" />,
      stats: '50+ Schools',
      link: '/programs/youth-development'
    },
    {
      id: 5,
      title: 'Environment',
      description: 'Promoting environmental conservation through tree plantation drives and sustainability initiatives.',
      icon: <Leaf className="w-8 h-8 text-[#da291c]" />,
      stats: '10,000+ Trees Planted',
      link: '/programs/environment'
    },
    {
      id: 6,
      title: 'Disaster Relief',
      description: 'Providing immediate assistance and rehabilitation support during natural disasters and emergencies.',
      icon: <ShieldCheck className="w-8 h-8 text-[#da291c]" />,
      stats: '50+ Relief Operations',
      link: '/programs/disaster-relief'
    }
  ];

  const stats = [
    { value: '15+', label: 'Years of Service' },
    { value: '50+', label: 'Active Programs' },
    { value: '500+', label: 'Dedicated Volunteers' },
    { value: '100K+', label: 'Lives Impacted' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 to-purple-900 text-white py-20">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('/images/pattern.png')] bg-cover"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Transforming Lives Through Our Programs
            </motion.h1>
            <motion.p 
              className="text-xl text-blue-100 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Discover how our initiatives are making a lasting impact in communities worldwide.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <a 
                href="#programs" 
                className="inline-block bg-[#da291c] hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-full transition-colors duration-300 transform hover:scale-105"
              >
                Explore Our Programs
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl font-bold text-[#da291c] mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section id="programs" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl mb-4">Our Impactful Programs</h2>
            <div className="h-1 w-20 bg-[#da291c] mx-auto"></div>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              We're committed to creating sustainable change through our diverse range of programs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <motion.div 
                key={program.id}
                className="group bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="p-1 bg-gradient-to-r from-blue-500 to-purple-600">
                  <div className="bg-white p-6 rounded-lg h-full">
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4 mx-auto group-hover:bg-blue-100 transition-colors duration-300">
                      {program.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{program.title}</h3>
                    <p className="text-gray-600 mb-4">{program.description}</p>
                    <div className="flex items-center justify-between mt-6">
                      <span className="text-sm font-medium text-[#da291c]">{program.stats}</span>
                      <a 
                        href={program.link} 
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        Learn more
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-800 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Make a Difference?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join us in our mission to create lasting change. Your support can transform lives and communities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/donate" 
              className="bg-[#da291c] hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-full transition-colors duration-300 inline-block"
            >
              Donate Now
            </a>
            <a 
              href="/volunteer" 
              className="bg-transparent hover:bg-white hover:bg-opacity-10 text-white font-semibold px-8 py-3 border-2 border-white rounded-full transition-colors duration-300 inline-block"
            >
              Become a Volunteer
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Programs;